/* ==========================================================================
   ss-promise: turns "Arrives in 2–5 business days" into expected dates,
   keeps the cash total in step, and lets the dates settle in.

   Motion source: Motion Primitives Text Effect (MIT), from
   D:\Claude Cowork\ui-libraries\motion-primitives @ 43a3188: text split into
   segments that animate in with a stagger, with the whole text kept for
   screen readers. Changed from its defaults:
     - split by word only, never by character, so Arabic stays joined
     - the "blur" / "fade-in-blur" presets (12 px blur, 20 px rise) -> a
       6 px drop and a fade, no blur: a foot settling into a footbed
     - 260 ms per word, 40 ms apart, damped; each time a new size is picked
     - React + AnimatePresence -> plain spans and element.animate()

   Dates are counted in Cairo time from the store's own promise (theme
   settings): the order day is today, or the next working day after the
   cut-off hour or on a day off or holiday; then the fastest and slowest
   business-day counts. They are worked out when shown, so a page left open
   past the cut-off, or overnight, never shows yesterday's dates.
   ========================================================================== */
(function () {
  'use strict';

  var SETTLE = 'cubic-bezier(0.22, 0.8, 0.26, 1)';
  var AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var all = [];

  function digits(value, arabic) {
    var s = String(value);
    return arabic ? s.replace(/[0-9]/g, function (d) { return AR_DIGITS[+d]; }) : s;
  }

  function part(parts, type) {
    for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value;
    return '';
  }

  // Today's date and hour in Cairo, as a UTC date holding Cairo's calendar day.
  function cairoNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Cairo',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23',
      }).formatToParts(new Date());
      var y = parseInt(part(parts, 'year'), 10);
      var m = parseInt(part(parts, 'month'), 10);
      var d = parseInt(part(parts, 'day'), 10);
      var h = parseInt(part(parts, 'hour'), 10) % 24;
      if (isNaN(y) || isNaN(m) || isNaN(d) || isNaN(h)) return null;
      return { day: new Date(Date.UTC(y, m - 1, d)), hour: h };
    } catch (e) {
      return null;
    }
  }

  function addDays(date, n) {
    var x = new Date(date.getTime());
    x.setUTCDate(x.getUTCDate() + n);
    return x;
  }

  function iso(date) {
    return date.toISOString().slice(0, 10);
  }

  function arrival(min, max, cutoff, off, holidays) {
    if (off.length >= 7) return null;
    var now = cairoNow();
    if (!now) return null;
    function isOff(date) {
      return off.indexOf(date.getUTCDay()) !== -1 || holidays.indexOf(iso(date)) !== -1;
    }
    var orderDay = now.day;
    var guard = 0;
    if (isOff(orderDay) || now.hour >= cutoff) {
      do { orderDay = addDays(orderDay, 1); } while (isOff(orderDay) && ++guard < 60);
    }
    function after(n) {
      var x = orderDay;
      var counted = 0;
      var steps = 0;
      while (counted < n && steps < 120) {
        x = addDays(x, 1);
        steps++;
        if (!isOff(x)) counted++;
      }
      return x;
    }
    return [after(min), after(max)];
  }

  // "Sun 28 Sep" in English, "الأحد ٢٨ سبتمبر" in Arabic.
  function label(date, arabic) {
    try {
      var parts = new Intl.DateTimeFormat(arabic ? 'ar-EG' : 'en-US', {
        timeZone: 'UTC',
        weekday: arabic ? 'long' : 'short',
        day: 'numeric',
        month: arabic ? 'long' : 'short',
      }).formatToParts(date);
      var text = [part(parts, 'weekday'), part(parts, 'day'), part(parts, 'month')].join(' ').trim();
      return text || null;
    } catch (e) {
      return null;
    }
  }

  // The words settle in one by one; a screen reader gets the whole line once.
  function settleWords(node, text, animate) {
    if (!animate) {
      node.textContent = text;
      return;
    }
    node.textContent = '';
    var said = document.createElement('span');
    said.className = 'ssr-visually-hidden';
    said.textContent = text;
    var shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    var words = text.split(' ');
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'ss-promise__w';
      span.textContent = word;
      shown.appendChild(span);
      if (i < words.length - 1) shown.appendChild(document.createTextNode(' '));
      if (span.animate) {
        span.animate(
          [
            { opacity: 0, transform: 'translateY(-6px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 260, delay: i * 40, easing: SETTLE, fill: 'backwards' }
        );
      }
    });
    node.appendChild(said);
    node.appendChild(shown);
  }

  function setup(el) {
    if (!el || el.__ssPromise) return;
    el.__ssPromise = true;
    var d = el.dataset;
    var arabic = d.locale === 'ar';
    var min = parseInt(d.min, 10) || 2;
    var max = parseInt(d.max, 10) || 5;
    var cutoff = parseInt(d.cutoff, 10);
    if (isNaN(cutoff)) cutoff = 15;
    var off = (d.off || '').split(',').filter(Boolean).map(Number);
    var holidays = String(d.offDates || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
    var ship = parseInt(d.ship, 10) || 0;
    var arrive = el.querySelector('[data-ss-arrive]');
    var sub = el.querySelector('[data-ss-arrive-sub]');
    var total = el.querySelector('[data-ss-total]');
    var wa = el.querySelector('[data-ss-wa]');
    var generic = arrive ? arrive.textContent : '';
    var shownText = null;
    var lastVariant = null;

    function datesText() {
      var dates = arrival(min, max, cutoff, off, holidays);
      if (!dates) return null;
      var a = label(dates[0], arabic);
      var b = label(dates[1], arabic);
      if (!a || !b) return null;
      if (a === b) return (d.tDate || '[a]').replace('[a]', a);
      return (d.tDates || '[a] – [b]').replace('[a]', a).replace('[b]', b);
    }

    // Worked out afresh every time, so the cut-off and the date are today's.
    function showDates(animate) {
      if (!arrive) return;
      var text = datesText();
      if (!text) return;
      if (text === shownText && !animate) return;
      shownText = text;
      settleWords(arrive, text, animate && !reduceMotion);
      if (sub) sub.hidden = false;
    }
    function hideDates() {
      if (shownText === null || !arrive) return;
      shownText = null;
      arrive.textContent = generic;
      if (sub) sub.hidden = true;
    }

    function money(cents) {
      var amount = Math.round(cents / 100);
      var text = amount >= 1000 ? amount.toLocaleString('en-US') : String(amount);
      return (d.moneyPrefix || '') + digits(text, arabic) + (d.moneySuffix || '');
    }

    el.__ssRefresh = function () {
      if (shownText !== null) showDates(false);
    };
    all.push(el);

    if (d.mode === 'cart' || d.ready === '1') showDates(false);

    // Cash total ("from" while sizes differ in price and none is picked) and
    // the WhatsApp message, with the colour and size once chosen.
    function applyPick(pick) {
      if (total && typeof pick.price === 'number') {
        var from = pick.varies && !pick.ready ? (d.tFrom || '') + ' ' : '';
        total.textContent = from + money(pick.price * (pick.qty || 1) + ship);
      }
      if (wa) {
        var template = wa.getAttribute('data-wa-template') || '';
        var message = template.replace('[pick]', pick.pickText || '');
        wa.href = 'https://wa.me/' + (wa.getAttribute('data-wa') || '') + '?text=' + encodeURIComponent(message);
      }
    }

    if (d.mode === 'product') {
      var product = el.closest('[data-ssr-product]');
      if (product) {
        // The state the page opened in: shown as it is, without motion.
        var first = product.__ssrPick;
        if (first) {
          if (first.ready) showDates(false);
          lastVariant = first.ready ? first.variant : null;
          applyPick(first);
        }
        product.addEventListener('ssr:pick', function (event) {
          var pick = event.detail;
          if (!pick) return;
          if (pick.ready) {
            // The dates settle in again each time a different size is picked.
            showDates(pick.variant !== lastVariant);
            lastVariant = pick.variant;
          } else {
            hideDates();
            lastVariant = null;
          }
          applyPick(pick);
        });
      }
    }
  }

  function init(scope) {
    var root = scope || document;
    if (root.matches && root.matches('[data-ss-promise]')) setup(root);
    Array.prototype.forEach.call(root.querySelectorAll('[data-ss-promise]'), setup);
  }

  // Back from the back/forward cache, or a tab brought back after a while:
  // the cut-off may have passed or the day changed.
  function refreshAll() {
    all.forEach(function (el) {
      if (el.isConnected && el.__ssRefresh) el.__ssRefresh();
    });
  }
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshAll();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refreshAll();
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  function start() {
    init();
    // The cart redraws its summary after every change; pick up the new one.
    if (document.querySelector('[data-ssr-cart]') && 'MutationObserver' in window) {
      new MutationObserver(function (records) {
        records.forEach(function (record) {
          Array.prototype.forEach.call(record.addedNodes, function (node) {
            if (node.nodeType === 1) init(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
